import React, { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Gift, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hakan-welcome-dismissed";

const WelcomeOffer = () => {
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);

  // Dismissal is tied to the offer version so re-enabling / editing in admin shows it again.
  const storageValue = offer?.updated_at || "1";

  useEffect(() => {
    axios
      .get(`${API}/settings/welcome`)
      .then(({ data }) => setOffer(data))
      .catch((e) => console.error("welcome offer failed", e));
  }, []);

  useEffect(() => {
    if (!offer || !offer.enabled) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(STORAGE_KEY) === storageValue;
    } catch {}
    if (dismissed) return;
    const t = setTimeout(() => setOpen(true), 2500);
    return () => clearTimeout(t);
  }, [offer, storageValue]);

  const remember = () => {
    try {
      localStorage.setItem(STORAGE_KEY, storageValue);
    } catch {}
  };

  const dismiss = (e) => {
    if (e) e.stopPropagation();
    setOpen(false);
    remember();
  };

  const goToContact = () => {
    setOpen(false);
    remember();
    document
      .getElementById("contact")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!offer || !offer.enabled) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="welcome-offer"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[380px]"
        >
          <div
            role="button"
            tabIndex={0}
            onClick={goToContact}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToContact()}
            data-testid="welcome-offer-cta"
            className="relative cursor-pointer overflow-hidden border border-[var(--hk-red)] bg-black shadow-[0_25px_60px_-15px_rgba(227,10,23,0.55)] group"
          >
            <button
              onClick={dismiss}
              aria-label="Close welcome offer"
              data-testid="welcome-offer-close"
              className="absolute top-2 right-2 z-10 p-1.5 text-white/60 hover:text-white bg-black/40 hover:bg-black/70 transition-colors"
            >
              <X size={14} />
            </button>

            {/* subtle red glow */}
            <div
              aria-hidden
              className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-30 group-hover:opacity-50 transition-opacity"
              style={{
                background:
                  "radial-gradient(circle, rgba(227,10,23,0.9), transparent 60%)",
              }}
            />
            <div aria-hidden className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--hk-red)]" />

            <div className="relative p-5 md:p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center w-8 h-8 bg-[var(--hk-red)]/15 border border-[var(--hk-red)]/50">
                  <Gift size={16} className="text-[var(--hk-red)]" />
                </span>
                <span data-testid="welcome-offer-overline" className="text-[10px] font-bold tracking-[0.35em] text-[var(--hk-red)]">
                  {offer.overline}
                </span>
              </div>

              <div data-testid="welcome-offer-title" className="font-display text-3xl md:text-4xl uppercase leading-[0.95] tracking-tight text-white mb-1">
                {offer.title}{" "}
                {offer.highlight && <span className="text-[var(--hk-red)]">{offer.highlight}</span>}
              </div>
              {offer.subtitle && (
                <div data-testid="welcome-offer-subtitle" className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/70 mb-4">
                  {offer.subtitle}
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <p data-testid="welcome-offer-text" className="text-xs text-white/60 leading-snug">
                  {offer.text}
                </p>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.28em] text-white group-hover:text-[var(--hk-red)] transition-colors whitespace-nowrap">
                  {offer.cta || "CLAIM"} <ArrowRight size={12} />
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeOffer;
